import React, { useRef, useEffect, useState } from 'react';
import { useNavigate, Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import './Header.scss';

// ---------------------------------------------------------
// SIDEBAR NAV GROUPS (investor)
// ---------------------------------------------------------
// The dashboard surfaces four "centres" as cards (My Documents, My Permissions,
// My Consents, Data Subject Rights). The sidebar mirrors them one-for-one so every
// centre is reachable without going back through the dashboard first.
interface SidebarChild {
  to: string;
  label: string;
  /** Match the path exactly (and with no query) — use when a sibling deep-links the same path. */
  exact?: boolean;
}

interface SidebarGroupDef {
  key: string;
  icon: string;
  label: string;
  children: SidebarChild[];
}

const INVESTOR_NAV_GROUPS: SidebarGroupDef[] = [
  {
    key: 'documents',
    icon: 'bi-folder2-open',
    label: 'My Documents',
    children: [
      // KYC Documents (/investor/documents) and Onboarding Documents (/investor/onboarding)
      // are JOURNEY-scoped pages — a global sidebar entry can't say which journey, so they
      // are reached from within a journey only (removed 2026-08-22, user decision).
      { to: '/investor/documents-center', label: 'Documents Center' },
    ],
  },
  {
    key: 'permissions',
    icon: 'bi-shield-check',
    label: 'My Permissions',
    children: [
      { to: '/investor/delegations', label: 'Permissions Center' },
      { to: '/investor/service-agent-activity', label: 'Agent Activity' },
    ],
  },
  {
    key: 'consents',
    icon: 'bi-ui-checks',
    label: 'My Consents',
    children: [
      // SOW entry removed 2026-08-23 (user): the SOW row lives in the Consent Centre table
      // and the journey gate asks for it contextually — a dedicated menu item was redundant.
      { to: '/investor/consents', label: 'Consent Centre' },
    ],
  },
  {
    key: 'dsr',
    icon: 'bi-person-lines-fill',
    label: 'My Rights',
    children: [
      { to: '/investor/dsr-center', label: 'New Request', exact: true },
      { to: '/investor/dsr-center/requests', label: 'View All Requests' },
    ],
  },
];

/** Does `pathname` sit on `path` or below it? ('/a/b' matches '/a/b' and '/a/b/1', not '/a/bc'). */
const onPath = (pathname: string, path: string) =>
  pathname === path || pathname.startsWith(`${path}/`);

const isChildActive = (child: SidebarChild, pathname: string, search: string): boolean => {
  const [path, query] = child.to.split('?');
  if (query) return pathname === path && search.includes(query);
  if (child.exact) return pathname === path && !search;
  return onPath(pathname, path);
};

const isGroupActive = (group: SidebarGroupDef, pathname: string): boolean =>
  group.children.some((c) => onPath(pathname, c.to.split('?')[0]));

interface HeaderProps {
  variant?: 'sidebar' | 'topbar' | 'legacy';
  title?: string;
  subtitle?: string;
  mobileSidebarOpen?: boolean;
  onToggleMobileSidebar?: () => void;
}

const Header: React.FC<HeaderProps> = ({ 
  variant = 'legacy', 
  title, 
  subtitle,
  mobileSidebarOpen = false,
  onToggleMobileSidebar
}) => {
  const { logout, userName, userRoles } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // Sidebar is icon-only at rest and expands on hover (CSS). Mirrored here so the
  // nav groups know whether their sub-menu is actually visible.
  const [navExpanded, setNavExpanded] = useState(true);
  const dropdownRef = useRef<HTMLLIElement | any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  // Admin console pages share this header but must not show the investor nav links.
  const isAdminArea = location.pathname.startsWith('/admin');
  // Service Agents get their own nav (dashboard / investors / audit / profile).
  const isServiceAgent = userRoles.includes('SERVICE_AGENT');
  // DSR admins land on the DSR dashboard; platform admins on the admin dashboard.
  const adminHome = userRoles.includes('DSR_ADMIN') ? '/admin/dsr-admins' : '/admin/dashboard';
  const homeHref = isAdminArea ? adminHome : isServiceAgent ? '/service-agent/dashboard' : '/investor/dashboard';
  const canManageDsrAdmins = userRoles.some((r) => r === 'ADMIN' || r === 'PLATFORM_SUPER_ADMIN');
  const roleLabel = userRoles.includes('PLATFORM_SUPER_ADMIN') ? 'Super Admin'
    : userRoles.includes('ADMIN') ? 'Platform Admin'
      : userRoles.includes('DSR_ADMIN') ? 'DSR Admin'
        : userRoles.includes('SERVICE_AGENT') ? 'Client Executive Agent'
          : 'Investor';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
  };

  // ---------------------------------------------------------
  // SIDEBAR VARIANT
  // ---------------------------------------------------------
  if (variant === 'sidebar') {
    return (
      <>
        {/* Backdrop for mobile drawer */}
        {mobileSidebarOpen && (
          <div 
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs z-40 lg:hidden transition-opacity"
            onClick={onToggleMobileSidebar}
            aria-hidden="true"
          />
        )}

        <aside
          className={`fixed left-0 top-0 h-screen w-[210px] border-r z-50 flex flex-col items-center transition-transform duration-300 ease-in-out ${
            mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          }`}
          style={{ background: 'linear-gradient(180deg, rgba(5, 47, 85, 1) 0%, rgba(5, 47, 85, 1) 60%, rgba(7, 87, 155, 1) 100%), radial-gradient(circle at 82% 20%, rgba(28, 166, 217, 1), transparent 34%)' }}
        >
          {/* Logo Section */}
          <div className="mb-0 w-full flex justify-between items-center bg-white px-3 py-1">
            <Link to={homeHref} className="flex items-center justify-center gap-2 my-1 flex-1">
              <img src="/assets/images/logo.png" alt="Logo" className="w-[60px] object-contain flex-shrink-0" />
              {location.pathname.startsWith('/investor/journey') && (
                <div className="flex items-center gap-2 font-bold text-[#052f55] text-xs whitespace-nowrap">
                  <span className="text-slate-300 font-normal">|</span>
                  <span>Facilon Status</span>
                </div>
              )}
            </Link>
            {/* Close button for mobile drawer */}
            <button
              onClick={onToggleMobileSidebar}
              className="lg:hidden text-slate-500 hover:text-slate-700 p-1 text-lg"
              aria-label="Close sidebar"
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>

          {/* Navigation Section */}
          <nav className="flex-1 w-full flex flex-col gap-1 overflow-y-auto overflow-x-hidden sidebar-nav-scroll">
            {isAdminArea ? (
              <>
                {canManageDsrAdmins && (
                  <>
                    <SidebarLink
                      to="/admin/dashboard"
                      icon="bi-speedometer2"
                      label="Admin Dashboard"
                    />
                    <SidebarAction
                      onClick={() => navigate('/admin/dashboard?action=register')}
                      icon="bi-person-plus"
                      label="Register DSR Admin"
                    />
                    <SidebarLink
                      to="/admin/clients"
                      icon="bi-people"
                      label="Investor Management"
                    />
                  </>
                )}
                <SidebarLink
                  to="/admin/dsr-admins"
                  icon="bi-clipboard-data"
                  label="DSR Dashboard"
                />
                <SidebarLink
                  to="/admin/dsr"
                  icon="bi-list-task"
                  label="Case Queue"
                />
                {canManageDsrAdmins && (
                  <SidebarLink
                    to="/admin/identity-reviews"
                    icon="bi-person-vcard"
                    label="Identity Review"
                  />
                )}
                {canManageDsrAdmins && (
                  <SidebarLink
                    to="/admin/audit"
                    icon="bi-clipboard-check"
                    label="Compliance Audit"
                  />
                )}
                {canManageDsrAdmins && (
                  <SidebarLink
                    to="/admin/form-templates"
                    icon="bi-file-earmark-pdf"
                    label="SP Form Templates"
                  />
                )}
              </>
            ) : isServiceAgent ? (
              <>
                <SidebarLink
                  to="/service-agent/dashboard"
                  icon="bi-grid"
                  label="Dashboard"
                />
                <SidebarLink
                  to="/service-agent/investors"
                  icon="bi-people"
                  label="My Investors"
                />
                <SidebarLink
                  to="/service-agent/introduce-investor"
                  icon="bi-person-plus"
                  label="Introduce Investor"
                />
                <SidebarLink
                  to="/service-agent/introduced-investors"
                  icon="bi-list-check"
                  label="My Introductions"
                  alsoActiveFor={['/service-agent/introduced-investors/']}
                />
                <SidebarLink
                  to="/service-agent/profile"
                  icon="bi-person"
                  label="My Profile"
                />
              </>
            ) : (
              <>
                <SidebarLink
                  to="/investor/dashboard"
                  icon="bi-grid"
                  label="Dashboard"
                />
                <SidebarLink
                  to="/investor/profile"
                  icon="bi-person"
                  label="My Profile"
                />
                {/* Points at the journeys list (user 2026-09-04) — progress lives per
                    journey there, not on the old aggregate /investor/progress page. */}
                <SidebarLink
                  to="/investor/journeys"
                  icon="bi-bar-chart"
                  label="My Journey"
                  // /investor/journey and /investor/journey/<id> are the SAME destination to an
                  // investor — the list and the journey they opened from it.
                  alsoActiveFor={['/investor/journey']}
                />
                {INVESTOR_NAV_GROUPS.map((group) => (
                  <SidebarGroup key={group.key} group={group} expanded={navExpanded} />
                ))}
              </>
            )}
          </nav>

          {/* Bottom Section */}
          <div className="mt-auto w-full px-2">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-start gap-2 p-3 rounded-xl text-sidebar-text hover:bg-sidebar-hover hover:text-white transition-all"
              title={t('logout')}
            >
              <i className="bi bi-box-arrow-right text-xl"></i>
              <span className="block font-medium text-[13px]">{t('logout')}</span>
            </button>
          </div>
        </aside>
      </>
    );
  }

  // ---------------------------------------------------------
  // TOPBAR VARIANT
  // ---------------------------------------------------------
  if (variant === 'topbar') {
    return (
      <header className="h-header bg-white border-b border-neutral-100 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-40">
        <div className="flex items-center gap-3">
          {/* Hamburger button for mobile devices */}
          <button
            onClick={onToggleMobileSidebar}
            className="lg:hidden p-2 rounded-lg text-neutral-600 hover:bg-neutral-100 transition-colors"
            aria-label="Toggle Navigation Menu"
          >
            <i className="bi bi-list text-2xl"></i>
          </button>

          <div>
            {title !== "" && (
              <h1 className="text-xl font-bold text-neutral-900 leading-tight mb-0">
                {title !== undefined ? title : 'Investor Console'}
              </h1>
            )}
            {title !== "" && subtitle && (
              <p className="text-sm text-neutral-500 font-medium mb-0">
                {subtitle}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button className="w-8 h-8 rounded-full flex items-center justify-center text-neutral-400 hover:bg-neutral-50 transition-colors relative">
            <i className="bi bi-bell text-lg"></i>
            <span className="absolute top-1 right-1 w-2 h-2 bg-primary-500 border-2 border-white rounded-full"></span>
          </button>

          <div className="h-8 w-px bg-neutral-200 mx-1"></div>

          <div className="flex items-center gap-2">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-bold text-neutral-900 leading-none mb-1">
                {userName || 'User'}
              </p>
              <p className="text-[10px] text-neutral-400 font-medium uppercase tracking-wider mb-0">
                {roleLabel}
              </p>
            </div>
            <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center text-white font-medium text-sm border border-neutral-200 bg-primary-900">
              {userName?.charAt(0) || 'U'}
            </div>
          </div>
        </div>
      </header>
    );
  }

  // ---------------------------------------------------------
  // LEGACY VARIANT (DEFAULT)
  // ---------------------------------------------------------
  return (
    <header className="facilon-header">
      <div className="container-fluid header-container">
        <div className="header-brand">
          <Link to={isAdminArea ? adminHome : '/investor/dashboard'} className="navbar-brand">
            <img src="/assets/images/facilon-main-logo.png" alt="Facilon Services" />
          </Link>
        </div>

        <nav className="header-nav">
          <ul className="nav-links">
            {!isAdminArea && (
              <>
                <li><NavLink to="/investor/dashboard" className="nav-link-item">Home</NavLink></li>
                <li><NavLink to="/investor/profile" className="nav-link-item">My Profile</NavLink></li>
                {/* /investor/progress was removed on 2026-09-04 when progress moved per-journey;
                    this link had been dead ever since. */}
                <li><NavLink to="/investor/journeys" className="nav-link-item">My Journey</NavLink></li>
                <li><NavLink to="/investor/delegations" className="nav-link-item">Service Agent</NavLink></li>
              </>
            )}

            <li ref={dropdownRef} className={`nav-dropdown ${isDropdownOpen ? 'active' : ''}`}>
              <button
                className="dropdown-trigger"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                aria-haspopup="true"
                aria-expanded={isDropdownOpen}
              >
                <i className="bi bi-person-circle me-2"></i>
                <span className="user-name">Welcome, {userName || 'User'}</span>
                <i className={`bi bi-chevron-down ms-2 ${isDropdownOpen ? 'rotate' : ''}`}></i>
              </button>

              {isDropdownOpen && (
                <div className="dropdown-menu-custom">
                  <Link to="/change-password" onClick={() => setIsDropdownOpen(false)} className="dropdown-item">
                    <i className="bi bi-shield-lock me-2"></i>
                    Change Password
                  </Link>
                  <button onClick={handleLogout} className="dropdown-item logout-btn">
                    <i className="bi bi-box-arrow-right me-2"></i>
                    {t('logout')}
                  </button>
                </div>
              )}
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
};

// Helper Component for Sidebar Links
//
// `alsoActiveFor` exists because a destination can span more than one route. NavLink marks
// itself active for its own path and its DESCENDANTS only, so "My Journey" -> /investor/journeys
// went dark the moment the investor opened a journey at /investor/journey/<id> — a sibling
// path, not a child, and the page where they spend nearly all their time (QA #9, measured
// 2026-09-12: aria-current="page" on the list, "none" on the detail page).
const SidebarLink: React.FC<{ to: string; icon: string; label: string; badge?: number; alsoActiveFor?: string[] }> = ({ to, icon, label, badge, alsoActiveFor }) => {
  const { pathname } = useLocation();
  const activeBySibling = (alsoActiveFor || []).some((p) => pathname === p || pathname.startsWith(p.endsWith('/') ? p : `${p}/`));
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `relative flex items-center justify-start px-3 py-2.5 rounded-lg ${isActive || activeBySibling
          ? 'bg-white/10 text-white font-bold'
          : 'text-[#8AABB5] hover:bg-white/5 hover:text-white font-medium'
        }`
      }
      title={label}
    >
      {({ isActive: navActive }) => {
        const isActive = navActive || activeBySibling;
        return (
        <>
          {/* Active border indicator */}
          {isActive && (
            <div className="absolute left-0 top-[10%] bottom-[10%] w-[4px] rounded-r-md bg-[#B0842B]" />
          )}
          <div className="flex items-center justify-start gap-2 w-full">
            <i className={`bi ${icon} text-[18px]`}></i>
            <span className="block text-[12px] whitespace-nowrap">{label}</span>
          </div>
          {badge !== undefined && (
            <span className="flex items-center justify-center bg-white/10 text-[11px] text-white px-2 py-0.5 rounded-full ml-auto">
              {badge}
            </span>
          )}
        </>
        );
      }}
    </NavLink>
  );
};

// Expandable sidebar section: a parent row (icon + label + caret) over a list of sub-links.
const SidebarGroup: React.FC<{ group: SidebarGroupDef; expanded: boolean }> = ({ group }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const groupActive = isGroupActive(group, location.pathname);
  const [open, setOpen] = useState(groupActive);

  // Landing on one of the group's pages (deep link, redirect) reveals the section.
  useEffect(() => {
    if (groupActive) setOpen(true);
  }, [groupActive]);

  const handleParentClick = () => {
    setOpen((o) => !o);
  };

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={handleParentClick}
        aria-expanded={open}
        className={`relative w-full flex items-center justify-start px-3 py-2.5 rounded-lg ${groupActive
          ? 'bg-white/10 text-white font-bold'
          : 'text-[#8AABB5] hover:bg-white/5 hover:text-white font-medium'
          }`}
        title={group.label}
      >
        {groupActive && (
          <div className="absolute left-0 top-[10%] bottom-[10%] w-[4px] rounded-r-md bg-[#B0842B]" />
        )}
        <div className="flex items-center justify-start gap-2 w-full">
          <i className={`bi ${group.icon} text-[18px]`}></i>
          <span className="block text-[12px] whitespace-nowrap">{group.label}</span>
        </div>
        <i
          className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'} block text-[11px] ml-auto flex-shrink-0`}
        ></i>
      </button>

      {open && (
        <div className="flex flex-col gap-0.5 mt-0.5 mb-1">
          {group.children.map((child) => (
            <SidebarSubLink
              key={child.to}
              child={child}
              active={isChildActive(child, location.pathname, location.search)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Sub-menu row. Active state is computed by the parent (children may deep-link the same
// path with a query, which NavLink's own matching cannot tell apart).
const SidebarSubLink: React.FC<{ child: SidebarChild; active: boolean }> = ({ child, active }) => {
  return (
    <Link
      to={child.to}
      className={`flex items-center gap-2 pl-[26px] pr-3 py-2 rounded-lg text-[13px] whitespace-nowrap no-underline ${active
        ? 'text-white font-semibold bg-white/10'
        : 'text-[#8AABB5] hover:bg-white/5 hover:text-white font-medium'
        }`}
      title={child.label}
    >
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${active ? 'bg-[#B0842B]' : 'bg-current opacity-60'}`} />
      {child.label}
    </Link>
  );
};

// Sidebar item that triggers an action instead of routing (e.g. opening a modal on the target page)
const SidebarAction: React.FC<{ onClick: () => void; icon: string; label: string }> = ({ onClick, icon, label }) => {
  return (
    <button
      onClick={onClick}
      className="relative w-full flex items-center justify-start px-3 py-2.5 rounded-lg text-[#8AABB5] hover:bg-white/5 hover:text-white font-medium"
      title={label}
    >
      <div className="flex items-center justify-start gap-2 w-full">
        <i className={`bi ${icon} text-[18px]`}></i>
        <span className="block text-[12px] whitespace-nowrap">{label}</span>
      </div>
    </button>
  );
};

export default Header;
